/**
 * "Donde aparece en el presupuesto" en Editar producto (MADERINCO-PRODUCTO-SECCION-CONTRATO-T1; Ares #30713).
 *
 * Diseno: Atenea WORK-PRODUCTO-SECCION-PASO_2026-10-19 (#30207, aceptado #30377). Contrato: nexo-api 568 (main):
 *   GET  /presupuestos/productos/{id}/ubicaciones -> {productoId, revision, ubicaciones[{seccionId, seccion, paso, orden, pregunta}]}
 *   PUT  igual, con {revision, ubicaciones[{seccionId, paso, orden, pregunta}]}: reemplaza TODO (lo que no viene se quita;
 *        lista vacia = sin ubicacion, pendiente); otra revision = 409 VERSION_CAMBIO; errores por fila en extensions.filas.
 * Reglas del diseno que este modulo hace cumplir antes de gastar el viaje:
 *   - cada fila tiene su seccion y su PROPIO paso (no hay paso global);
 *   - una seccion no se repite (una ubicacion por seccion; dos papeles en la misma seccion no se deciden solos);
 *   - una fila incompleta no se guarda como valida: el error va en esa fila.
 * Sin DOM: lo prueba check-ubicaciones-producto.
 *
 * @module application/presupuestos/ubicacionesProducto
 */

export const PASOS = [
  { id: 'tipo', etiqueta: '1. Tipo' },
  { id: 'revision_obligatoria', etiqueta: '2. Revisión obligatoria', ayuda: 'Obliga a revisar esta parte; no obliga a comprar este producto.' },
  { id: 'complemento', etiqueta: '3. Complemento' }
];
const PASO = Object.fromEntries(PASOS.map((p) => [p.id, p]));
const de = (o, k) => (o ? (o[k] ?? o[k[0].toUpperCase() + k.slice(1)]) : undefined);

/** La respuesta de la API en una forma (camelCase o PascalCase). */
export function leerUbicaciones(data) {
  const d = data?.data ?? data ?? {};
  const rev = Number(de(d, 'revision'));
  return {
    revision: Number.isInteger(rev) ? rev : null,
    filas: (de(d, 'ubicaciones') || []).map((u) => ({
      seccionId: String(de(u, 'seccionId') ?? ''), seccion: String(de(u, 'seccion') ?? ''),
      paso: String(de(u, 'paso') ?? ''), pregunta: de(u, 'pregunta') ?? null, orden: de(u, 'orden') ?? null
    }))
  };
}

/**
 * Revisa las filas y arma el cuerpo del PUT. Devuelve {ok, cuerpo} o {ok:false, errores: {indiceDeFila: frase}}.
 * @param {Array<{seccionId:string, paso:string, orden?:number, pregunta?:string}>} filas
 */
export function cuerpoUbicaciones(filas, revision) {
  const errores = {};
  const vistas = new Map();
  (filas || []).forEach((f, i) => {
    if (!f.seccionId) errores[i] = 'Elige la sección.';
    else if (!PASO[f.paso]) errores[i] = 'Elige en qué paso aparece.';
    else if (vistas.has(f.seccionId)) errores[i] = `Esa sección ya está en la fila ${vistas.get(f.seccionId) + 1}: un producto aparece una vez por sección.`;
    if (f.seccionId && !vistas.has(f.seccionId)) vistas.set(f.seccionId, i);
  });
  if (Object.keys(errores).length) return { ok: false, errores };
  return {
    ok: true,
    cuerpo: { revision, ubicaciones: (filas || []).map((f) => ({ seccionId: f.seccionId, paso: f.paso, orden: f.orden ?? null, pregunta: f.pregunta ?? null })) }
  };
}

/**
 * Los errores que manda la API (extensions.filas). CONTRATO, no adivinanza (obs de Q #30753): la API cuenta las filas
 * DESDE 0 (ProductoUbicacionesService.Validar: Fila = i) y usa Fila = -1 para un error del BLOQUE (DEMASIADAS).
 * Antes se adivinaba la base y un error de la fila 2 caia en la fila 1; el -1 se perdia sin mensaje.
 * @returns {{filas: Object<number,string>, bloque: string|null}}
 */
export function erroresDeLaApi(detalles, nFilas) {
  const lista = detalles?.filas || detalles?.extensions?.filas || detalles?.Filas || [];
  const filas = {};
  const bloque = [];
  for (const x of lista) {
    const i = Number(de(x, 'fila'));
    const msg = String(de(x, 'mensaje') || de(x, 'codigo') || 'Revisa esta fila.');
    if (Number.isInteger(i) && i >= 0 && i < nFilas) filas[i] = msg;
    else bloque.push(msg);
  }
  return { filas, bloque: bloque.length ? bloque.join(' · ') : null };
}

/** "Cubierta → 1. Tipo → Teja de barro"; sin filas: el aviso de pendiente (no se oculta ni se infiere seccion). */
export function vistaPrevia(filas, nombreProducto, nombreDeSeccion) {
  const validas = (filas || []).filter((f) => f.seccionId && PASO[f.paso]);
  if (!validas.length) return ['Todavía sin ubicación: queda pendiente de clasificar y se encuentra por búsqueda.'];
  return validas.map((f) => `${nombreDeSeccion(f.seccionId) || f.seccion || 'Sección'} → ${PASO[f.paso].etiqueta} → ${nombreProducto || 'este producto'}`);
}

/** Lo que cambio, en una linea (se conserva si el guardado falla). */
export function resumenDelCambio(antes, despues) {
  const k = (f) => f.seccionId + '|' + f.paso;
  const A = new Set((antes || []).map(k));
  const D = new Set((despues || []).filter((f) => f.seccionId && PASO[f.paso]).map(k));
  const nuevas = [...D].filter((x) => !A.has(x)).length;
  const quitadas = [...A].filter((x) => !D.has(x)).length;
  if (!nuevas && !quitadas) return '';
  return [nuevas ? `${nuevas} ubicación${nuevas > 1 ? 'es' : ''} nueva${nuevas > 1 ? 's' : ''} o cambiada${nuevas > 1 ? 's' : ''}` : '', quitadas ? `${quitadas} quitada${quitadas > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ');
}
